import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { Company } from '../types/CompanyTypes';
import { Job } from '../types/JobTypes';
import { CountryData } from '../data/countryData';

type ComparisonItem = CountryData | Company | Job;

interface ComparisonState {
  countries: CountryData[];
  companies: Company[];
  jobs: Job[];
}

interface ComparisonContextType {
  state: ComparisonState;
  addToComparison: (type: 'country' | 'company' | 'job', item: any) => boolean;
  removeFromComparison: (type: 'country' | 'company' | 'job', id: string) => void;
  clearComparison: (type: 'country' | 'company' | 'job') => void;
  isInComparison: (type: 'country' | 'company' | 'job', id: string) => boolean;
  getComparisonCount: () => { countries: number; companies: number; jobs: number; total: number };
  canAddToComparison: (type: 'country' | 'company' | 'job') => boolean;
}

const MAX_COMPARISON_ITEMS = 6;

const ComparisonContext = createContext<ComparisonContextType | null>(null);

export const useComparison = () => {
  const context = useContext(ComparisonContext);
  if (!context) {
    throw new Error('useComparison must be used within a ComparisonProvider');
  }
  return context;
};

export const ComparisonProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<ComparisonState>(() => {
    // Load from localStorage on initialization
    const saved = localStorage.getItem('comparison');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Ensure all arrays exist
        return {
          countries: parsed.countries || [],
          companies: parsed.companies || [],
          jobs: parsed.jobs || []
        };
      } catch (e) {
        console.error('Failed to parse saved comparison data:', e);
      }
    }
    return {
      countries: [],
      companies: [],
      jobs: []
    };
  });

  // Save to localStorage whenever state changes
  useEffect(() => {
    // console.log('[ComparisonContext] State updated:', state);
    localStorage.setItem('comparison', JSON.stringify(state));
  }, [state]);

  const addToComparison = useCallback((type: 'country' | 'company' | 'job', item: any): boolean => {
    // console.log(`[ComparisonContext] Adding ${type}:`, item);
    
    setState(prev => {
      // Fix the key to ensure proper pluralization
      const key = type === 'country' ? 'countries' : 
                  type === 'company' ? 'companies' : 
                  'jobs' as keyof ComparisonState;
      const currentItems = prev[key] as any[];
      
      // Check if currentItems exists
      if (!currentItems) {
        // console.log(`[ComparisonContext] No current items, creating new array`);
        return {
          ...prev,
          [key]: [item]
        };
      }
      
      // Check if already in comparison
      const itemIdentifier = type === 'country' ? item.code : item.id;
      // console.log(`[ComparisonContext] Item identifier for ${type}: ${itemIdentifier}`);
      
      const exists = currentItems.some((i: any) => {
        const currentIdentifier = type === 'country' ? i.code : i.id;
        // console.log(`[ComparisonContext] Comparing ${currentIdentifier} === ${itemIdentifier}`);
        return currentIdentifier === itemIdentifier;
      });
      
      if (exists) {
        // console.log(`[ComparisonContext] Item already exists in comparison`);
        return prev;
      }
      
      if (currentItems.length >= MAX_COMPARISON_ITEMS) {
        // console.log(`[ComparisonContext] Max items (${MAX_COMPARISON_ITEMS}) reached`);
        return prev;
      }

      // console.log(`[ComparisonContext] Adding item, new count will be:`, currentItems.length + 1);
      return {
        ...prev,
        [key]: [...currentItems, item]
      };
    });
    return true;
  }, []);

  const removeFromComparison = useCallback((type: 'country' | 'company' | 'job', id: string) => {
    setState(prev => {
      const key = type === 'country' ? 'countries' : 
                  type === 'company' ? 'companies' : 
                  'jobs' as keyof ComparisonState;
      const items = prev[key] as any[];
      if (!items || !Array.isArray(items)) return prev;
      
      return {
        ...prev,
        [key]: items.filter((item: any) => 
          type === 'country' ? item.code !== id : item.id !== id
        )
      };
    });
  }, []);

  const clearComparison = useCallback((type: 'country' | 'company' | 'job') => {
    const key = type === 'country' ? 'countries' : 
                type === 'company' ? 'companies' : 
                'jobs' as keyof ComparisonState;
    setState(prev => ({
      ...prev,
      [key]: []
    }));
  }, []);

  const isInComparison = useCallback((type: 'country' | 'company' | 'job', id: string): boolean => {
    const key = type === 'country' ? 'countries' : 
                type === 'company' ? 'companies' : 
                'jobs' as keyof ComparisonState;
    const items = state[key] as any[];
    if (!items || !Array.isArray(items)) return false;
    return items.some((item: any) => 
      type === 'country' ? item.code === id : item.id === id
    );
  }, [state]);

  const getComparisonCount = useCallback(() => {
    return {
      countries: state.countries?.length || 0,
      companies: state.companies?.length || 0,
      jobs: state.jobs?.length || 0,
      total: (state.countries?.length || 0) + (state.companies?.length || 0) + (state.jobs?.length || 0)
    };
  }, [state]);

  const canAddToComparison = useCallback((type: 'country' | 'company' | 'job'): boolean => {
    const key = type === 'country' ? 'countries' : 
                type === 'company' ? 'companies' : 
                'jobs' as keyof ComparisonState;
    const items = state[key];
    return (items?.length || 0) < MAX_COMPARISON_ITEMS;
  }, [state]);

  const value: ComparisonContextType = {
    state,
    addToComparison,
    removeFromComparison,
    clearComparison,
    isInComparison,
    getComparisonCount,
    canAddToComparison
  };

  return (
    <ComparisonContext.Provider value={value}>
      {children}
    </ComparisonContext.Provider>
  );
};